import { Async, constants, IndexFS, InMemory, Inode } from '@zenfs/core';
import { basename, dirname, join } from '@zenfs/core/path';
import { log, withErrno } from 'kerium';
import { alert } from 'kerium/log';
import { _throw } from 'utilium';
import { convertException } from './utils.js';
function _isShared(buffer) {
    return typeof buffer == 'object' && buffer !== null && buffer.constructor.name == 'SharedArrayBuffer';
}
function isResizable(buffer) {
    if (buffer instanceof ArrayBuffer)
        return buffer.resizable;
    if (_isShared(buffer))
        return buffer.growable;
    return false;
}
/**
 * Since `FileSystemHandle.kind` doesn't have correct type support
 */
function isKind(handle, kind) {
    return handle.kind == kind;
}
/**
 * @todo Consider supporting synchronous stuff with `FileSystemFileHandle.createSyncAccessHandle()`\
 * @internal
 */
export class WebAccessFS extends Async(IndexFS) {
    /**
     * Loads all of the handles.
     * @internal @hidden
     */
    async _loadHandles(path, handle) {
        for await (const [key, child] of handle.entries()) {
            const p = join(path, key);
            this._handles.set(p, child);
            if (isKind(child, 'directory'))
                await this._loadHandles(p, child);
        }
    }
    /**
     * Loads metadata
     * @internal @hidden
     */
    async _loadMetadata(metadataPath) {
        this._handles.set('/', this.root);
        await this._loadHandles('/', this.root);
        if (metadataPath) {
            const handle = await this.get('file', metadataPath);
            const file = await handle.getFile();
            const raw = await file.text();
            const data = JSON.parse(raw);
            this.index.fromJSON(data);
            return;
        }
        for (const [path, handle] of this._handles) {
            if (isKind(handle, 'file')) {
                const { lastModified, size } = await handle.getFile();
                this.index.set(path, new Inode({ mode: 0o644 | constants.S_IFREG, size, mtimeMs: lastModified }));
                continue;
            }
            if (!isKind(handle, 'directory'))
                throw withErrno('EIO', 'Invalid handle');
            this.index.set(path, new Inode({ mode: 0o777 | constants.S_IFDIR, size: 0 }));
        }
    }
    constructor(root, disableHandleCache = false) {
        super(0x77656261, 'webaccessfs');
        this.root = root;
        this.disableHandleCache = disableHandleCache;
        /**
         * Used to speed up handle lookups.
         * Without this, every lookup would be O(n) on the path length.
         * With the cache, these become O(1) operations.
         */
        this._handles = new Map();
        /**
         * @hidden
         */
        this._sync = InMemory.create({ label: 'accessfs-cache' });
        this.attributes.set('no_buffer_resize', true);
        if (disableHandleCache)
            this.attributes.set('no_handle_cache', true);
    }
    async stat(path) {
        try {
            return await super.stat(path);
        }
        catch (ex) {
            if (ex.code != 'ENOENT')
                throw ex;
            // This handle must have been created after initialization
            // Try to add a new inode for the handle to the index
            const handle = await this.get(null, path);
            const inode = new Inode();
            if (isKind(handle, 'file')) {
                const file = await handle.getFile();
                inode.update({
                    mode: 0o644 | constants.S_IFREG,
                    size: file.size,
                    mtimeMs: file.lastModified,
                });
            }
            else {
                inode.update({
                    mode: constants.S_IFDIR | 0o777,
                    size: 0,
                });
            }
            this.index.set(path, inode);
            return inode;
        }
    }
    async readdir(path) {
        const dirHandle = await this.get('directory', path);
        return await Array.fromAsync(dirHandle.keys());
    }
    async remove(path) {
        const handle = await this.get('directory', dirname(path));
        for (const p of this._handles.keys())
            if (dirname(p) == path)
                this._handles.delete(p);
        this._handles.delete(path);
        await handle.removeEntry(basename(path), { recursive: true }).catch(ex => _throw(convertException(ex, path)));
    }
    removeSync() {
        throw log.crit(withErrno('ENOSYS'));
    }
    async read(path, buffer, offset, end) {
        if (end <= offset)
            return;
        const handle = await this.get('file', path);
        const file = await handle.getFile();
        const data = await file.arrayBuffer();
        if (data.byteLength < end - offset)
            throw alert(withErrno('EIO', [
                'Unexpected mismatch in file data size. This should not happen.',
                `tried to read ${end - offset} bytes but the file is ${data.byteLength} bytes.`,
                `path: ${path}`,
            ].join('\n' + ' '.repeat(24))));
        buffer.set(new Uint8Array(data, offset, end - offset));
    }
    async write(path, buffer, offset) {
        if (isResizable(buffer.buffer) || _isShared(buffer.buffer)) {
            const newBuffer = new Uint8Array(new ArrayBuffer(buffer.byteLength), buffer.byteOffset, buffer.byteLength);
            newBuffer.set(buffer);
            buffer = newBuffer;
        }
        const inode = this.index.get(path);
        if (!inode)
            throw withErrno('ENOENT');
        const isDir = (inode.mode & constants.S_IFMT) == constants.S_IFDIR;
        let handle;
        try {
            handle = await this.get(isDir ? 'directory' : 'file', path);
        }
        catch {
            const parent = await this.get('directory', dirname(path));
            handle = await parent[isDir ? 'getDirectoryHandle' : 'getFileHandle'](basename(path), { create: true }).catch((ex) => _throw(convertException(ex, path)));
            if (!this.disableHandleCache)
                this._handles.set(path, handle);
        }
        if (isDir)
            return;
        if (isKind(handle, 'directory')) {
            log.crit(withErrno('EIO', 'Mismatch in entry kind on write'));
            return;
        }
        const writable = await handle.createWritable({ keepExistingData: true });
        if (offset) {
            try {
                await writable.seek(offset);
            }
            catch {
                await writable.write({ type: 'seek', position: offset });
            }
        }
        await writable.write(buffer); // We convert to a non-shared buffer above
        await writable.close();
        const { size, lastModified } = await handle.getFile();
        inode.update({ size, mtimeMs: lastModified });
        this.index.set(path, inode);
    }
    /**
     * Do not use!
     * @deprecated @internal @hidden
     */
    async writeFile(path, data) {
        return this.write(path, data, 0);
    }
    async _mkdir(path) {
        const handle = await this.get('directory', dirname(path));
        const dir = await handle.getDirectoryHandle(basename(path), { create: true }).catch((ex) => _throw(convertException(ex, path)));
        if (!this.disableHandleCache)
            this._handles.set(path, dir);
    }
    async get(kind = null, path) {
        const maybeHandle = this._handles.get(path);
        if (!this.disableHandleCache && maybeHandle) {
            if (kind && !isKind(maybeHandle, kind))
                throw withErrno(kind == 'directory' ? 'ENOTDIR' : 'EISDIR');
            return maybeHandle;
            // Otherwise, fall through to the slow path
        }
        if (path == '/')
            return this.root;
        const parts = path.slice(1).split('/');
        let dir = this.root;
        for (let i = 0; i < parts.length - 1; ++i) {
            dir = await dir.getDirectoryHandle(parts[i]).catch(ex => _throw(convertException(ex, path)));
        }
        try {
            const handle = await dir[kind == 'file' ? 'getFileHandle' : 'getDirectoryHandle'](parts.at(-1));
            if (!this.disableHandleCache)
                this._handles.set(path, handle);
            return handle;
        }
        catch (ex) {
            if (ex.name != 'TypeMismatchError')
                throw convertException(ex, path);
            else if (kind === null) {
                const handle = await dir.getFileHandle(parts.at(-1)).catch(ex => _throw(convertException(ex, path)));
                if (!this.disableHandleCache)
                    this._handles.set(path, handle);
                return handle;
            }
            else
                throw withErrno(kind == 'file' ? 'EISDIR' : 'ENOTDIR');
        }
    }
}
const _WebAccess = {
    name: 'WebAccess',
    options: {
        handle: { type: 'object', required: true },
        metadata: { type: 'string', required: false },
        disableHandleCache: { type: 'boolean', required: false },
    },
    async create(options) {
        const fs = new WebAccessFS(options.handle, options.disableHandleCache);
        await fs._loadMetadata(options.metadata);
        return fs;
    },
};
export const WebAccess = _WebAccess;
